import { useQueryClient } from "@tanstack/react-query";
import { Languages } from "lucide-react";
import { cn } from "@tarawa/ui";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";

/** FR ⇄ AR toggle. Also stores the preference on the account (used for SMS language). */
export function LanguageSwitch({ className, full }: { className?: string; full?: boolean }) {
  const { locale, setLocale, t } = useI18n();
  const { customer } = useAuth();
  const qc = useQueryClient();

  const change = (l: "fr" | "ar") => {
    if (l === locale) return;
    setLocale(l);
    if (customer) {
      void api("/account", { method: "PATCH", body: { locale: l } })
        .then(() => qc.invalidateQueries({ queryKey: ["me"] }))
        .catch(() => undefined);
    }
  };

  if (!full) {
    return (
      <button
        onClick={() => change(locale === "fr" ? "ar" : "fr")}
        className={cn(
          "flex h-10 items-center gap-1.5 rounded-full bg-surface px-3 text-sm font-semibold text-ink shadow-[var(--shadow-card)] hover:bg-brand-50",
          className,
        )}
        aria-label={t("common.language")}
      >
        <Languages className="size-4 text-brand-600" />
        {locale === "fr" ? "العربية" : "Français"}
      </button>
    );
  }

  return (
    <div className={cn("grid grid-cols-2 gap-2 rounded-2xl bg-canvas p-1", className)} role="radiogroup" aria-label={t("common.language")}>
      {(["fr", "ar"] as const).map((l) => (
        <button
          key={l}
          role="radio"
          aria-checked={locale === l}
          onClick={() => change(l)}
          className={cn(
            "h-11 rounded-xl text-sm font-semibold transition",
            locale === l ? "bg-surface text-brand-700 shadow-sm" : "text-muted",
          )}
        >
          {l === "fr" ? "Français" : "العربية"}
        </button>
      ))}
    </div>
  );
}
